import classNames from 'classnames';
import React, {
  HTMLProps,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';
import { CheckIcon, GlobeIcon } from '@stpkg/stand-react';
import css from './GlobalNav.module.scss';
import UserMenu from './UserMenu';
import { LocaleContext } from '../../../contexts/LocaleContext';
import { useAuth } from '../../../hooks/api/auth';
import { useUserMe } from '../../../hooks/api/me';
import Link from 'next/link';
import { RSButton } from 'src/reservation-page/RSButton';
import { buildSiteUrl, buildUrl } from 'src/utils/request-url';

interface Props extends HTMLProps<{}> {
  logoImage: { url: string } | null;
  merchantPublicId: string | null;
  displayName: string | null;
}

const LocaleArray = [
  {
    value: 'ja',
    label: '日本語',
  },
  {
    value: 'en',
    label: 'English',
  },
  {
    value: 'ko',
    label: '한국어',
  },
] as const;
//  satisfies { value: string; label: string }[]

const GlobalNav: React.FC<Props> = ({
  className,
  logoImage,
  merchantPublicId,
  displayName,
}) => {
  const intl = useIntl();
  const { user, isLoggedIn } = useUserMe();
  const { logout } = useAuth();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isOpenLocalSettings, setIsOpenLocalSettings] =
    useState<boolean>(false);
  const localSettingsRef = useRef<HTMLDivElement>(null);
  const localeCtx = useContext(LocaleContext);

  const toggleUserMenu = useCallback((flag: boolean) => {
    setIsActive(flag);
  }, []);

  const intlPref = 'components.common.layouts.globalHeader.';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        localSettingsRef.current &&
        !localSettingsRef.current.contains(event.target as Node)
      ) {
        setIsOpenLocalSettings(false);
      }
    };

    if (isOpenLocalSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpenLocalSettings]);

  return (
    <nav className={classNames(css.GlobalNav, className)}>
      <div className={css.GlobalNav__Inner}>
        {logoImage && merchantPublicId ? (
          <a href={buildSiteUrl()} className={css.GlobalNav__Logo}>
            <div>
              <img
                src={logoImage.url}
                alt="logo"
                className={css.GlobalNav__LogoImage}
              />
            </div>
          </a>
        ) : (
          <Link
            href={merchantPublicId ? buildSiteUrl('', 'next') : '/'}
            className={css.GlobalNav__DisplayName}
          >
            {displayName}
          </Link>
        )}

        <div className="flex items-center gap-1">
          <div className="text-16 pc:text-15 relative flex items-center gap-0.5">
            <button
              className="rounded-15 hover:bg-gray-10 flex cursor-pointer items-center justify-center border-none bg-white p-1 outline-none transition focus:outline focus-visible:outline-2 focus-visible:outline-blue-700"
              onClick={() => setIsOpenLocalSettings(true)}
            >
              <GlobeIcon
                className="fill-blue-700"
                width="20"
                height="20"
                viewBox="0 0 48 48"
              />
            </button>
            {isOpenLocalSettings && (
              <div
                ref={localSettingsRef}
                role="presentation"
                className="z-5 rounded-15 absolute top-[48px] w-[max-content] bg-white p-1 drop-shadow-lg"
              >
                <ul className="m-0 list-none p-0">
                  {LocaleArray.map((locale) => {
                    return (
                      <li
                        key={locale.value}
                        aria-current={
                          localeCtx.currentLocale === locale.value
                            ? 'true'
                            : 'false'
                        }
                        lang={locale.value}
                      >
                        <button
                          className={classNames(
                            'rounded-10 text-gray-1000 text-16 pc:text-15 flex w-full cursor-pointer items-center gap-1 border-none bg-white py-1.5 pr-1 hover:bg-gray-100',
                            localeCtx.currentLocale === locale.value
                              ? 'pl-1'
                              : 'pl-[36px]',
                          )}
                          onClick={() => {
                            localeCtx.changeLocale(locale.value);
                          }}
                        >
                          {localeCtx.currentLocale === locale.value && (
                            <span className="inline-flex items-center">
                              <CheckIcon
                                className="fill-blue-700"
                                width="20"
                                height="20"
                                viewBox="0 0 48 48"
                              />
                            </span>
                          )}
                          <span>{locale.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <div className={css.GlobalNav__Buttons}>
            {isLoggedIn && (
              <a href={buildUrl('/_')} className={css.GlobalNav__TextLink}>
                {intl.formatMessage({ id: intlPref + 'links.dashboard' })}
              </a>
            )}
            {isLoggedIn && user && (
              <UserMenu
                user={user}
                isActive={isActive}
                toggle={toggleUserMenu}
                logout={async () => {
                  await logout();
                  location.href = buildUrl(
                    `/signin/user?return_to=${location.pathname}`,
                  );
                }}
              />
            )}
            {!isLoggedIn && (
              <div className={css.GlobalNav__LoginButtonWrapper}>
                <RSButton
                  variant="outline"
                  onClick={() => {
                    location.href = buildUrl(
                      `/signin/user?return_to=${location.pathname}`,
                    );
                  }}
                >
                  {intl.formatMessage({ id: intlPref + 'buttons.signin' })}
                </RSButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default GlobalNav;
